export const AUTH_STORAGE_KEY = "nazare-session";

export type AuthSession = {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  user: unknown;
};

type StorageLike = Pick<Storage, "getItem" | "removeItem" | "setItem">;
type Fetcher = typeof fetch;

const LOGIN_MUTATION = `
  mutation Login($code: String!, $redirectUri: String!) {
    loginWithGoogle(googleAuthCodeInput: { code: $code, redirectUri: $redirectUri }) {
      accessToken
      refreshToken
      expiresAt
      user
    }
  }
`;

const REFRESH_MUTATION = `
  mutation RefreshToken($token: String!) {
    refreshToken(refreshRequestInput: { refreshToken: $token }) {
      accessToken
      refreshToken
      expiresAt
      user
    }
  }
`;

function normaliseExpiry(value: number): number {
  return value < 1e12 ? value * 1000 : value;
}

function isAuthSession(value: unknown): value is AuthSession {
  if (!value || typeof value !== "object") return false;
  const session = value as AuthSession;
  return (
    typeof session.accessToken === "string" &&
    typeof session.refreshToken === "string" &&
    typeof session.expiresAt === "number"
  );
}

export function loadAuthSession(storage: StorageLike): AuthSession | null {
  const value = storage.getItem(AUTH_STORAGE_KEY);
  if (!value) return null;

  try {
    const session: unknown = JSON.parse(value);
    if (!isAuthSession(session)) return null;
    return { ...session, expiresAt: normaliseExpiry(session.expiresAt) };
  } catch {
    return null;
  }
}

export function storeAuthSession(storage: StorageLike, session: AuthSession): AuthSession {
  const normalised = { ...session, expiresAt: normaliseExpiry(session.expiresAt) };
  storage.setItem(AUTH_STORAGE_KEY, JSON.stringify(normalised));
  return normalised;
}

export function clearAuthSession(storage: StorageLike): void {
  storage.removeItem(AUTH_STORAGE_KEY);
}

export function isUsableAuthSession(session: AuthSession): boolean {
  return session.expiresAt > Date.now();
}

async function mutateAuth(
  apiDomain: string,
  query: string,
  variables: Record<string, string>,
  operation: "loginWithGoogle" | "refreshToken",
  fetcher: Fetcher,
  bearer?: string,
): Promise<AuthSession> {
  const response = await fetcher(`${apiDomain}/graphql`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
    },
    body: JSON.stringify({ query, variables }),
  });
  if (!response.ok) throw new Error(`Authentication failed with status ${response.status}.`);

  const payload: {
    data?: Partial<Record<typeof operation, Partial<AuthSession> | null>>;
  } = await response.json();
  const session = payload.data?.[operation];
  if (!isAuthSession(session)) throw new Error("Authentication did not return a complete session.");
  return { ...session, expiresAt: normaliseExpiry(session.expiresAt) };
}

export function loginWithGoogle(
  code: string,
  apiDomain: string,
  redirectUri: string,
  fetcher: Fetcher = fetch,
): Promise<AuthSession> {
  return mutateAuth(apiDomain, LOGIN_MUTATION, { code, redirectUri }, "loginWithGoogle", fetcher);
}

export function refreshAuthSession(
  session: AuthSession,
  apiDomain: string,
  fetcher: Fetcher = fetch,
): Promise<AuthSession> {
  return mutateAuth(
    apiDomain,
    REFRESH_MUTATION,
    { token: session.refreshToken },
    "refreshToken",
    fetcher,
    session.refreshToken,
  );
}

export function createSessionHandoffUrl(returnUrl: URL, session: AuthSession): string {
  const destination = new URL(returnUrl);
  const handoff = new URLSearchParams(destination.hash.slice(1));
  handoff.set("auth_session", JSON.stringify(session));
  destination.hash = handoff.toString();
  return destination.href;
}
